<template>
  <header class="bg-white w-full print:hidden sm:relative" :class="{ 'sm:hidden': onHomePage }">
    <div class="flex h-[65px] min-h-[65px] sm:h-[122px] sm:min-h-[122px]">
      <Teleport v-if="show_visualiser_modal" defer to="#portal_popup">
        <SaveVisualisation :show-modal="show_visualiser_modal" @close="show_visualiser_modal = false" />
      </Teleport>
      <div v-if="authStore.isBrandedModeUser" class="flex grow">
        <Logo
          v-if="!isIframe()"
          class="border-b sm:border-r"
          :sidebar-visible="sidebarVisible"
          :src="customerStore.customer_logo || styleStore.customer_logo"
          :customer-name="customerStore.customer_name"
          :href="brandedStore.branded_mode_settings.webAddress"
        />
        <div v-show="brandedStore.branded_mode_settings.phone" class="sm:flex flex-col space-around border-b grow">
          <div
            class="hidden 2xs:block my-auto px-2 mx-2 md:px-10 md:mx-0 2xs:py-4 sm:whitespace-nowrap text-xs 2xs:text-sm"
          >
            <span :class="{ 'hidden sm:inline': (hasPricing && onDesignerPage) || visualiser_place_page }">
              Need help? Call us on <strong> {{ brandedStore.branded_mode_settings.phone }} </strong>
            </span>
          </div>
        </div>
      </div>
      <div v-else class="flex grow">
        <router-link to="/">
          <Logo
            class="border-b xs:border-r"
            :sidebar-visible="sidebarVisible"
            :src="styleStore.fabricator_logo"
            :customer-name="customerStore.customer_name"
          />
        </router-link>
        <div class="flex grow flex-col border-b sm:border-b-0">
          <div>
            <slot name="header_page_breadcrumb" />
          </div>
          <div class="hidden sm:block grow">
            <slot name="site_header_actions_wrapper">
              <div class="flex border-b flex-1 h-full">
                <div class="flex flex-col justify-around" :class="{ 'hidden  xs:flex': showSearch }">
                  <div class="text-lg font-medium px-3 py-2 sm:text-xl md:text-2xl md:px-10">
                    <slot name="header_page_title" />
                  </div>
                </div>
                <div v-show="showSearch" class="flex flex-col justify-around grow px-3" style="max-width: 600px">
                  <div id="portal_search" class="block" />
                </div>
                <div class="ml-auto flex flex-col justify-around px-3 py-2">
                  <slot name="header_page_actions" />
                </div>
              </div>
            </slot>
          </div>
        </div>
      </div>
      <div class="flex">
        <div v-if="visualiser_place_page" class="text-center flex justify-around pl-4 pr-4 flex-col border-l border-b">
          <button class="btn btn-action hidden xs:flex btn-lg" @click="saveVisualisation()">Save Visualisation</button>
          <button class="btn btn-action xs:hidden" @click="saveVisualisation()">Save</button>
        </div>
        <HeaderSaveToQuote v-if="onDesignerPage" class="hidden xs:flex" />
        <BasketPopup
          v-if="showBasket"
          :not_on_basket_page="notOnBasketPage"
          :on_home_page="onHomePage"
          :on_designer_page="onDesignerPage"
        />
        <a
          v-if="onDesignerPage && !authStore.isBrandedModeUser"
          class="flex flex-col justify-center p-4 text-center border-l border-b cursor-pointer h-full w-16 sm:w-32"
          @click="designAnother()"
        >
          <div class="text-2xl">
            <i class="fal fa-repeat-alt" />
          </div>
          <div class="hidden sm:block text-sm capitalize mt-3">
            <div>Switch</div>
            <div>Product</div>
          </div>
        </a>
        <MenuPopup v-if="showPopupMenu" class="sm:hidden" />
        <a
          v-if="authStore.isBrandedModeUser && !isIframe()"
          :href="brandedStore.branded_mode_settings.webAddress"
          class="flex-col justify-center p-4 text-center border-l border-b cursor-pointer h-full w-16 sm:w-32"
          :class="[visualiser_place_page ? 'hidden xs:flex' : 'flex']"
        >
          <div class="text-2xl">
            <i class="fal fa-browser" />
          </div>
          <div class="hidden sm:block text-sm capitalize mt-3">
            <div>Back To</div>
            <div>Website</div>
          </div>
        </a>
      </div>
    </div>
    <div
      v-if="!['/', '/visualiser', '/visualiser/place'].includes($route.path) && !authStore.isBrandedModeUser"
      class="sm:hidden"
    >
      <slot name="site_header_actions_wrapper">
        <div class="w-full flex border-b flex-1 h-full py-2">
          <div class="flex flex-col justify-around" :class="{ 'hidden  xs:flex': showSearch }">
            <div class="text-lg font-medium px-3 py-2 sm:text-xl md:text-2xl md:px-10">
              <slot name="header_page_title" />
            </div>
          </div>
          <div v-show="showSearch" class="flex flex-col justify-center grow px-3 mx-auto" style="max-width: 600px">
            <div id="portal_search" class="block" />
          </div>
          <div class="ml-auto flex flex-col justify-around">
            <slot name="header_page_actions" />
          </div>
        </div>
      </slot>
    </div>
  </header>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/pinia/auth'
import { useBrandedStore } from '@/pinia/branded'
import { useCustomerStore } from '@/pinia/customer'
import { useStyleStore } from '@/pinia/style'
import { useBasketStore } from '@/pinia/basket'
import { useVisualiserStore } from '@/pinia/visualiser'
import { useDesignerStore } from '@/pinia/designer'
import { isIframe } from '@/utils/web'
import SaveVisualisation from '@/components/branded/visualiser/SaveVisualisation.vue'
import BasketPopup from '@/components/shared/nav/BasketPopup.vue'
import HeaderSaveToQuote from '@/components/shared/nav/HeaderSaveToQuote.vue'
import Logo from '@/components/shared/nav/Logo.vue'
import MenuPopup from '@/components/shared/nav/MenuPopup.vue'
import alertbox from '@/utils/alertbox'

export default {
  name: 'SiteHeader',
  components: {
    BasketPopup,
    HeaderSaveToQuote,
    SaveVisualisation,
    Logo,
    MenuPopup
  },
  props: ['sidebarVisible', 'visualiser_place_page'],
  setup() {
    return { isIframe }
  },
  data() {
    return {
      show_visualiser_modal: false
    }
  },
  computed: {
    ...mapStores(
      useBrandedStore,
      useCustomerStore,
      useBasketStore,
      useVisualiserStore,
      useDesignerStore,
      useStyleStore,
      useAuthStore
    ),
    requiresMeasurements() {
      return this.basketStore.currentItem.dimensions.some(
        (dimension) => dimension.requiresInput && !dimension.hasBeenInput
      )
    },
    requiresInput() {
      return this.basketStore.currentItem.specification.some((spec) => spec.mustBeSelected && !spec.hasBeenSelected)
    },
    showBasket() {
      return this.authStore.loggedIn && !this.authStore.isBusinessUser && this.$route.name !== 'Job'
    },
    showPopupMenu() {
      return this.authStore.loggedIn && !this.authStore.isBrandedModeUser
    },
    showDashboardLink() {
      return this.authenticated_user && !this.authStore.isBrandedModeUser
    },
    showSearch() {
      return this.$route.meta.showSearch === true
    },
    onHomePage() {
      return ['Portal Dashboard', 'Admin Dashboard'].includes(this.$route.name)
    },
    onDesignerPage() {
      return ['Design Online - Designer', 'Customer Designer', 'Designer'].includes(this.$route.name)
    },
    onChooseProductPage() {
      return ['Customer Choose Product', 'Design Online - Choose Product'].includes(this.$route.name)
    },
    notOnBasketPage() {
      return (
        this.$route.name !== 'Basket' &&
        this.$route.name !== 'Checkout' &&
        this.$route.name !== 'Design Online - Basket' &&
        this.$route.name !== 'Thank You'
      )
    },
    hasPricing() {
      return !this.authStore.isBrandedModeUser || this.customerStore.hasBrandedPricingOn
    }
  },
  methods: {
    async saveVisualisation() {
      if (this.authStore.isBrandedModeUser) {
        this.show_visualiser_modal = true
      } else {
        this.visualiserStore.saveToServer()
        const basketUrl = this.authStore.isBusiness
          ? `/customer/${this.basketStore.customerId}/sales-dashboard/basket`
          : '/basket'
        await this.$router.push(basketUrl)
        await alertbox({
          title: 'Visualisation Saved',
          text: 'Visualisation Saved to Quote.  Please complete your quote as usual.'
        })
      }
    },
    async designAnother() {
      const result = await alertbox({
        title: 'Switch Product',
        text: 'Would you like to go back and select a different product?',
        icon: 'fa-regular fa-question-circle text-blue-500',
        buttons: [
          { text: 'Yes, and delete my current product', style: 'btn-secondary', action: { isDelete: true } },
          { text: 'Yes, but save my current product', style: 'btn-action' }
        ],
        isClosable: true
      })
      if (result?.isCancelled) {
        return
      }

      if (result?.isDelete) {
        await this.basketStore.deleteLineItem({
          lineItemId: this.$route.params.id
        })
      } else if (this.requiresInput) {
        await alertbox({ title: 'You must answer the required questions before adding this to your basket' })
        this.designerStore.SET_OPEN_MENU_TYPE('specification')
        return
      } else if (this.requiresMeasurements) {
        await alertbox({ title: 'You must supply dimensions before adding this to your basket' })
        this.designerStore.SET_OPEN_MENU_TYPE('sizes')
        return
      }

      if (this.authStore.isBrandedModeUser) {
        this.$router.push(this.brandedStore.chooseProductURL)
      } else if (this.authStore.isBusiness) {
        this.$router.push(`/customer/${this.basketStore.customerId}/sales-dashboard/choose-product`)
      } else {
        this.$router.push('/choose-product')
      }
    }
  }
}
</script>
